import {
  ActionColumnPermission,
  actionColumn,
} from "@/components/Table/table.interface";
import { MODULES } from "@/constants";
import routes from "@/routes";
import { Module } from "@/types";
import formatNumber from "@/utils/formatNumber";
import dayjs from "dayjs";
import { IPermissions } from "../User/user.interface";

export const regions = [
  { label: "North America", value: "NAM" },
  { label: "Europe", value: "EMEA" },
  { label: "Asia Pacific", value: "APAC" },
];
export const displayFieldsList = [
  {
    name: "regions",
    label: "Select Region(s)",
    placeholder: "Region",
    itemList: regions,
    required: true,
    valueContainsLabel: false,
  },
  {
    name: "jobFunctions",
    label: "Select Job Functions",
    placeholder: "Function",
    valueContainsLabel: false,
    itemList: [
      {
        label: "Consulting",
        value: "Consulting",
      },
      {
        label: "Engineering & Technical",
        value: "Engineering & Technical",
      },
      {
        label: "Finance",
        value: "Finance",
      },
      {
        label: "General Management",
        value: "General Management",
      },
      {
        label: "Human Resources",
        value: "Human Resources",
      },
      {
        label: "Legal",
        value: "Legal",
      },
      {
        label: "Marketing",
        value: "Marketing",
      },
      {
        label: "Medical & Health",
        value: "Medical & Health",
      },
      {
        label: "Operations",
        value: "Operations",
      },
      {
        label: "Sales",
        value: "Sales",
      },
      {
        label: "Scientists",
        value: "Scientists",
      },
    ],
    required: true,
  },
  {
    name: "managementLevel",
    label: "Select Job Level",
    placeholder: "Level",
    valueContainsLabel: false,
    itemList: [
      {
        label: "Non-Manager",
        value: "Non-Manager",
      },
      {
        label: "Manager",
        value: "Manager",
      },
      {
        label: "Director",
        value: "Director",
      },
      {
        label: "VP-Level",
        value: "VP-Level",
      },
      {
        label: "C-Level",
        value: "C-Level",
      },
      {
        label: "Staff",
        value: "Staff",
      },
      {
        label: "CxO",
        value: "CxO",
      },
    ],
    required: true,
  },
];

export const linkedInFieldsList = [
  {
    name: "dmpSegments",
    label: "Edit a Matched Audience (Optional)",
    placeholder: "segment",
    valueContainsLabel: false,
    required: false,
  },
  {
    name: "locations",
    label: "Enter Location",
    placeholder: "Location",
    valueContainsLabel: false,
    required: false,
  },
  {
    name: "jobFunctions",
    label: "Select Job Functions",
    placeholder: "Function",
    required: true,
    valueContainsLabel: false,
  },
  {
    name: "managementLevel",
    label: "Select Job Level",
    placeholder: "Level",
    required: true,
    valueContainsLabel: false,
  },
  {
    name: "titles",
    label: "Enter Job Title",
    placeholder: "Title",
    required: false,
    valueContainsLabel: false,
  },
  {
    name: "ageRanges",
    label: "Enter Age Ranges",
    placeholder: "Age Ranges",
    required: false,
    valueContainsLabel: false,
  },
  {
    name: "industries",
    label: "Enter Industries",
    placeholder: "Industries",
    required: false,
    valueContainsLabel: false,
  },
  {
    name: "genders",
    label: "Enter Gender",
    placeholder: "Gender",
    required: false,
    valueContainsLabel: false,
  },
  {
    name: "skills",
    label: "Enter Skills",
    placeholder: "Skill",
    required: false,
    valueContainsLabel: false,
  },
  {
    name: "yearOfExperienceList",
    label: "Enter year of experience",
    placeholder: "Year of experience",
    required: false,
    valueContainsLabel: false,
  },
];

/** How long an audience stays usable after its last update. */
const AUDIENCE_LIFETIME_DAYS = 90;

/**
 * Timestamps render through here so all three audience lists agree, and so the
 * one place that formats them stays separate from the raw value the column
 * sorts on. Never sort the output of this — see the `expiresIn` header.
 */
export function formatAudienceDate(value?: string | null) {
  return value ? dayjs(value).format("MM/DD/YYYY - HH:mm:ss A") : "";
}

/**
 * What a numeric cell shows when the figure has not been produced yet. Matches
 * `useAdvertiserName`, which already returns "-" for the same idea.
 *
 * Not "0": the sort reads the same value and treats absent as absent, so it has
 * no place in the ordering and goes to the bottom whichever way the column is
 * pointed. Printing "0" put a row at the foot of an ascending numeric sort
 * showing a value smaller than everything above it, and made a real zero and a
 * missing one indistinguishable on screen while they sorted to opposite ends.
 *
 * Not blank either: a pending audience has none of these figures, so a whole
 * run of empty cells reads as a table that failed to render rather than one
 * saying it has nothing to report.
 */
export const NO_VALUE = "-";

/**
 * Sorts an Advertiser column on the name, with the unresolved rows treated as
 * absent rather than as a row literally called "-".
 *
 * `useAdvertiserName` returns `NO_VALUE` when a row has no advertiser to resolve
 * — agency-owned records under "All Advertisers" — and the sort has no way to
 * tell that from real text. Left alone, `localeCompare` orders punctuation ahead
 * of letters and the whole unresolved block landed at the TOP of an ascending
 * sort, while the identical absent state in every numeric column was being
 * pushed to the bottom. Mapping it back to empty puts them together.
 */
export const advertiserSortKey =
  (advertiserName: (row: any) => string) => (row: any) => {
    const name = advertiserName(row);
    return name === NO_VALUE ? "" : name;
  };

export function formatAudienceCount(value: number | null | undefined) {
  return value === null || value === undefined
    ? NO_VALUE
    : formatNumber({ value, style: "decimal" });
}

/** Coverage, which is a ratio rather than a count. Same absent marker. */
export function formatAudiencePercent(value: number | null | undefined) {
  return value === null || value === undefined ? NO_VALUE : value.toFixed(2);
}

export function expiresInLabel(updatedAt?: string | null) {
  if (!updatedAt) return "";
  const remainingDays =
    AUDIENCE_LIFETIME_DAYS - dayjs().diff(dayjs(updatedAt), "days");
  if (remainingDays <= 0) return "Expired";
  if (remainingDays === 1) return "1 day";
  return `${remainingDays} days`;
}

/**
 * The Advertiser column exists only for agency users. An advertiser-scoped user
 * has a disabled, single-value filter, so the column would repeat one name — and
 * worse, `useAdvertisersList` resolves advertisers as `parentCompanyId: <own id>`
 * for them, which matches nothing, so every cell would render "-". Dropping it
 * does not cause a layout shift for that user: they cannot switch advertisers,
 * so their column set is fixed for the whole session.
 *
 * The header array is the SINGLE owner of which columns exist — the row writes
 * `<Cell columnId="advertiser">` unconditionally and `CustomTable` drops the
 * header and the cell together. A row that re-tested the condition would put
 * one end out of step with the other, and every column-position rule in the
 * table with it.
 */
const advertiserHeader = {
  id: "advertiser",
  label: "Advertiser",
  width: 170,
  wrap: true,
};

/**
 * How a column's value is read off a row.
 *
 * Account and Contact audiences share these columns and their labels but NOT
 * their data shape — Account reads a merged Beeswax/LinkedIn payload nested
 * under `output` with per-channel key names, Contact reads flat top-level
 * fields. So the shape is declared once here and each list supplies its own
 * accessors.
 *
 * The same accessor feeds the cell and the sort. That is the point of the
 * indirection: the pair drifting apart is exactly how these columns ended up
 * unsortable in the first place — the sort read `row["coverage"]`, which does
 * not exist on either shape, while the cell computed the number from two other
 * fields.
 */
export interface AudienceRowValues {
  /**
   * The word the Status cell prints, not the raw field.
   *
   * The two lists collapse status differently — Account maps everything that
   * isn't "completed" onto "pending" including a missing one, Contact prints
   * blank when it is missing — and the sort has to see whatever the cell
   * decided. Reading the raw field instead sorted rows whose status was null
   * into a separate group from rows showing the same word, which left
   * descending order reading pending, pending, completed, completed, completed,
   * pending, pending, pending.
   */
  status: (row: any) => string;
  totalDomain: (row: any) => number | null;
  coveredDomain: (row: any) => number | null;
  coverage: (row: any) => number | null;
  userCount: (row: any) => number | null;
  deviceCount: (row: any) => number | null;
}

/**
 * Widths are exact — `CustomTable` switches to `table-layout: fixed` once every
 * column declares one. They sum past the viewport on purpose: the action column
 * stays pinned while the rest scrolls, and the column picker is there for anyone
 * who wants a narrower table.
 *
 * Created/Updated/Expires used to live in a collapsible panel under each row.
 * They are columns now — three values behind a click, on a table that can
 * already sort and hide columns, was the whole reason these lists needed a
 * different table component from every other list in the app.
 */
export function accountOrEmailHeaders({
  showAdvertiser,
  values,
  advertiserName,
  actionPermission,
  includeExpiry = false,
}: {
  showAdvertiser: boolean;
  values: AudienceRowValues;
  advertiserName: (row: any) => string;
  /**
   * Omit entirely when the row holds a button no `PermissionGuard` covers.
   *
   * This used to be a bare module that both lists were gated on, which is wrong
   * for Account: its Refresh button carries no guard, so a user holding only
   * `read` lost the whole column — and with it a button they were still allowed
   * to press. `actionColumn`'s own documentation says as much. Contact's buttons
   * are all guarded, so it still declares one.
   */
  actionPermission?: ActionColumnPermission;
  /**
   * Whether to show the 90-day Expires In column.
   *
   * Account only. That lifetime came off the Account row's own detail panel;
   * nothing in the codebase ties it to contact audiences, so showing it there
   * would have told a user their still-valid audience had expired.
   */
  includeExpiry?: boolean;
}) {
  return [
    ...(showAdvertiser
      ? [{ ...advertiserHeader, sortKey: advertiserSortKey(advertiserName) }]
      : []),
    {
      id: "segment",
      label: "Segment",
      width: 230,
      wrap: true,
      grow: true,
      // Same fallback the cell renders. They have to match: sorting on a field
      // the cell never prints puts an apparently blank row in the middle of the
      // order instead of at the end with the other empties.
      sortKey: (row: any) => row.segmentName ?? row.name,
    },
    {
      id: "status",
      label: "Status",
      width: 110,
      sortKey: values.status,
    },
    // Renders a channel logo, not text: opting out of the ellipsis rules keeps
    // `overflow: hidden` from clipping the image instead of ellipsising it.
    { id: "channel", label: "Channel", width: 100, noTruncate: true },
    {
      id: "totalDomain",
      label: "Total Domains",
      width: 130,
      sortKey: values.totalDomain,
    },
    {
      id: "coveredDomain",
      label: "Covered Domains",
      width: 150,
      sortKey: values.coveredDomain,
    },
    {
      id: "coverage",
      label: "Coverage (%)",
      width: 130,
      sortKey: values.coverage,
    },
    {
      id: "userCount",
      label: "User Count",
      width: 120,
      sortKey: values.userCount,
    },
    {
      id: "deviceCount",
      label: "Device Count",
      width: 130,
      sortKey: values.deviceCount,
    },
    // Sorted on the raw timestamp, never the rendered "MM/DD/YYYY - ..." string,
    // which does not sort chronologically as text.
    //
    // 190 because the rendered value is a fixed length — every one of these is
    // exactly "MM/DD/YYYY - HH:mm:ss A", so a width that clips one clips all of
    // them, and there is nothing to gain by sizing for a shorter case that never
    // occurs.
    { id: "createdAt", label: "Created At", width: 190 },
    { id: "updatedAt", label: "Updated At", width: 190 },
    ...(includeExpiry
      ? [
          {
            id: "expiresIn",
            label: "Expires In",
            width: 120,
            toolTip: `Audiences expire ${AUDIENCE_LIFETIME_DAYS} days after they were last updated.`,
            // Days remaining is a function of `updatedAt`, so ordering by it is
            // the same ordering — oldest update expires first. Sorting the
            // rendered "12 days" / "Expired" string would put "9 days" after
            // "12 days".
            sortKey: "updatedAt",
          },
        ]
      : []),
    // Default width: these are icon buttons now, at most three of them. 280 was
    // sized for the text buttons they replaced.
    actionColumn(actionPermission ?? {}),
  ];
}

export function retargetingHeaders(
  showAdvertiser: boolean,
  advertiserName: (row: any) => string,
  audienceType: (row: any) => string
) {
  return [
    ...(showAdvertiser
      ? [{ ...advertiserHeader, sortKey: advertiserSortKey(advertiserName) }]
      : []),
    {
      id: "segment",
      label: "Segment",
      width: 280,
      wrap: true,
      grow: true,
      sortKey: (row: any) => row.segmentName ?? row.name,
    },
    /*
      Off by default. Almost every row on this list is `display`, so the icon
      repeats down the whole column and earns none of its width; the few
      LinkedIn / HeyReach / SmartLead rows are worth confirming occasionally
      rather than reading every time. Seeded only — once the user touches the
      picker their choice takes over. See ITableHeaders.defaultHidden.
    */
    {
      id: "channel",
      label: "Channel",
      width: 100,
      noTruncate: true,
      defaultHidden: true,
    },
    // Sorted on the label the cell shows, not `row.type`: two of the three
    // values are mapped through AUDIENCE_TYPE_MAPPING and the third is a
    // fallback for a missing `type`, so the raw field orders nothing.
    { id: "type", label: "Type", width: 230, sortKey: audienceType },
    // Vendor-pushed audiences only; every other row is blank here. Position must
    // match the row's <Cell columnId="status"> — head and cells pair by index.
    { id: "status", label: "Status", width: 190, wrap: true },
    // Fixed-length rendered value — see the note on the same pair above.
    { id: "createdAt", label: "Created At", width: 190 },
    { id: "updatedAt", label: "Updated At", width: 190 },
    // Run on `create`, Delete on `delete`. Edit is commented out in the row; if
    // it comes back it is gated on `update` and belongs in this union.
    actionColumn({
      module: MODULES.AUDIENCE_SITE_VISITOR,
      actions: ["create", "delete"],
    }),
  ];
}
export function permittedAudienceTabs(
  permission: IPermissions,
  isAdmin: boolean
) {
  const tabList: any[] = [];
  Object.keys(AUDIENCE_TABS).forEach((module) => {
    if (isAdmin || permission[module as keyof IPermissions]) {
      tabList.push(AUDIENCE_TABS[module as keyof typeof AUDIENCE_TABS]);
    }
  });
  return tabList;
}
export const AUDIENCE_TABS = {
  AUDIENCE_ACCOUNT: {
    to: routes.AUDIENCES.ACCOUNT_AUDIENCE.INDEX,
    label: "Account Audience",
    filter: true,
  },
  AUDIENCE_EMAIL: {
    to: routes.AUDIENCES.EMAIL_AUDIENCES.INDEX,
    label: "Contact Audience",
    filter: true,
  },
  AUDIENCE_SITE_VISITOR: {
    to: routes.AUDIENCES.RETARGETING.INDEX,
    label: "Retargeting Audience",
    filter: true,
  },
  AUDIENCE_MANAGER: {
    to: routes.AUDIENCE_MANAGER.INDEX,
    label: "Audience Manager",
    filter: true,
  },
};

/**
 * `hasScope` is true when the list has something to query for — either a
 * concrete advertiser or the agency-wide "All Advertisers" selection.
 */
export function emptyTableText(hasScope: boolean, isLoading: boolean) {
  if (hasScope && !isLoading) {
    return "No Data Found";
  }

  return "Select an Advertiser";
}
export const audienceTerms = (adAccountId: string) =>
  `https://business.facebook.com/ads/manage/customaudiences/tos/?act=${adAccountId}`;
